import { Tables, putItem, scanAll, getItem, updateItem } from "./db";
import { getAllCommits } from "./commits";
import { triggerPipelineWithVersion } from "./pipeline";
import { rollbackPipelineStage } from "./rollback";
import type { Version, Commit, Stage, VersionStatus } from "../types";

/** Get all versions from DynamoDB */
export async function getAllVersions(): Promise<Version[]> {
  const items = await scanAll(Tables.VERSIONS);
  return (items as Version[]).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

/** Get a single version */
export async function getVersion(id: string): Promise<Version | undefined> {
  const item = await getItem(Tables.VERSIONS, { id });
  return item as Version | undefined;
}

/** 
 * Get the version currently deployed at a stage.
 * 
 * A version is "at" a stage if:
 * - It was deployed there (deployedAt is set) AND
 * - It hasn't been rolled back AND
 * - No newer version has successfully deployed to that same stage since
 * 
 * Note: A version that promoted beyond this stage still counts as deployed here
 * unless a different version was deployed to this stage after it.
 */
export async function getLatestByStage(stage: Stage): Promise<Version | undefined> {
  const versions = await getAllVersions();
  const stageOrder: Stage[] = ["dev", "beta", "prod"];
  const stageIdx = stageOrder.indexOf(stage);

  // A version is active at this stage if:
  // 1. Its current stage IS this stage (deployed/awaiting-promotion/deploying here now)
  // 2. OR its current stage is BEYOND this stage (it passed through here and is still valid)
  //    meaning it deployed here successfully at some point and wasn't rolled back
  
  // First check: any version whose current stage is exactly this one
  const atThisStage = versions.find(
    (v) => v.stage === stage && (v.status === "deployed" || v.status === "awaiting-promotion" || v.status === "deploying")
  );
  if (atThisStage) return atThisStage;

  // Second check: a version that has moved past this stage (promoted further)
  // is still running at this stage unless something else replaced it
  const promoted = versions.find(
    (v) => stageOrder.indexOf(v.stage) > stageIdx && 
           (v.status === "deployed" || v.status === "awaiting-promotion" || v.status === "deploying") &&
           v.deployedAt // it actually deployed at some point
  );
  if (promoted) return promoted;

  return undefined;
}

/** Get versions in the deploy queue (queued or deploying) */
export async function getDeployQueue(): Promise<Version[]> {
  const versions = await getAllVersions();
  return versions.filter((v) => v.status === "queued" || v.status === "deploying");
}

/** Generate a changelog summary from a list of commits */
export function generateChangelog(commits: Commit[]): string {
  if (commits.length === 0) return "No changes.";

  const lines: string[] = [];
  const features = new Set<string>();
  const fixes = new Set<string>();
  const other: string[] = [];

  for (const commit of commits) {
    const msg = commit.message.split("\n")[0]; // First line only

    if (commit.linkedFeatures.length > 0 || msg.toLowerCase().startsWith("feat")) {
      features.add(msg.replace(/^feat[:(]?\s*/i, ""));
    } else if (commit.linkedBugs.length > 0 || msg.toLowerCase().startsWith("fix")) {
      fixes.add(msg.replace(/^fix[:(]?\s*/i, ""));
    } else {
      other.push(msg);
    }
  }

  if (features.size > 0) {
    lines.push("## Features");
    features.forEach((f) => lines.push(`- ${f}`));
    lines.push("");
  }

  if (fixes.size > 0) {
    lines.push("## Bug Fixes");
    fixes.forEach((f) => lines.push(`- ${f}`));
    lines.push("");
  }

  if (other.length > 0) {
    lines.push("## Other Changes");
    other.forEach((o) => lines.push(`- ${o}`));
    lines.push("");
  }

  lines.push(`---`);
  lines.push(`${commits.length} commit${commits.length === 1 ? "" : "s"} included in this release.`);

  return lines.join("\n");
}

/**
 * Create a new version from selected commits.
 * Status starts as "created" — not yet deployed.
 * The most recent commit in the selection becomes the targetCommitId
 * (the pipeline will build at that point in history).
 */
export async function createVersion(data: {
  version: string;
  commitIds: string[];
  autoApprove?: { beta: boolean; prod: boolean };
}): Promise<Version> {
  const allCommits = await getAllCommits();
  const selectedCommits = allCommits.filter((c) => data.commitIds.includes(c.commitId));

  // Sort by timestamp to find the latest commit in the selection
  const sorted = [...selectedCommits].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
  const targetCommitId = sorted[0]?.commitId || data.commitIds[0];

  // Aggregate linked features/bugs
  const linkedFeatures = [...new Set(selectedCommits.flatMap((c) => c.linkedFeatures))];
  const linkedBugs = [...new Set(selectedCommits.flatMap((c) => c.linkedBugs))];

  const changelog = generateChangelog(selectedCommits);

  const version: Version = {
    id: `ver-${Date.now()}`,
    version: data.version,
    stage: "dev",
    status: "created",
    createdAt: new Date().toISOString(),
    commits: data.commitIds,
    changelog,
    linkedFeatures,
    linkedBugs,
    targetCommitId,
    autoApprove: data.autoApprove || { beta: false, prod: false },
  };

  await putItem(Tables.VERSIONS, version);
  return version;
}

/**
 * Queue a version for deployment.
 * Triggers the pipeline targeting this version's commit.
 */
export async function deployVersion(id: string): Promise<Version | undefined> {
  const version = await getVersion(id);
  if (!version) return undefined;
  if (version.status !== "created" && version.status !== "failed") {
    throw new Error(`Cannot deploy version in status "${version.status}"`);
  }

  // Trigger pipeline with the target commit
  const executionId = await triggerPipelineWithVersion(version.targetCommitId);

  // Update version status
  await updateItem(Tables.VERSIONS, { id }, {
    status: "deploying" as VersionStatus,
    pipelineExecutionId: executionId,
    stage: "dev",
  });

  return { ...version, status: "deploying", pipelineExecutionId: executionId, stage: "dev" };
}

/**
 * Mark a version as deployed (called after pipeline succeeds at a stage).
 */
export async function markDeployed(id: string, stage: Stage): Promise<void> {
  await updateItem(Tables.VERSIONS, { id }, {
    status: "deployed" as VersionStatus,
    stage,
    deployedAt: new Date().toISOString(),
  });
}

/**
 * Mark a version as failed.
 */
export async function markFailed(id: string): Promise<void> {
  await updateItem(Tables.VERSIONS, { id }, { status: "failed" as VersionStatus });
}

/** Promote a version to the next stage (triggers pipeline from approval) */
export async function promoteVersion(id: string, toStage: Stage): Promise<Version | undefined> {
  const existing = await getVersion(id);
  if (!existing) return undefined;

  await updateItem(Tables.VERSIONS, { id }, {
    stage: toStage,
    status: "deploying" as VersionStatus,
  });

  return { ...existing, stage: toStage, status: "deploying" };
}

/**
 * Roll back a stage to a previous version.
 * Uses CodePipeline RollbackStage to revert to the previous successful execution.
 * 
 * @param targetStage - The stage to roll back (dev, beta, prod)
 * @returns The version that was rolled back
 */
export async function rollbackStage(targetStage: Stage): Promise<Version | undefined> {
  const versions = await getAllVersions();

  // Find the currently deployed version at this stage
  const current = versions.find(
    (v) => v.stage === targetStage && (v.status === "deployed" || v.status === "awaiting-promotion")
  );
  if (!current) throw new Error(`No deployed version found at ${targetStage}`);

  // Trigger pipeline rollback
  const executionId = await rollbackPipelineStage(targetStage);

  // Mark current as rolled back
  await updateItem(Tables.VERSIONS, { id: current.id }, {
    status: "rolled-back" as VersionStatus,
    pipelineExecutionId: executionId,
  });

  return { ...current, status: "rolled-back", pipelineExecutionId: executionId };
}

/**
 * Roll back to a specific version at a specific stage.
 * Uses CodePipeline's RollbackStage to revert to the previous
 * successful execution — deploys prior artifacts directly without
 * re-running the full pipeline.
 */
export async function rollbackToVersion(
  versionId: string,
  targetStage: Stage
): Promise<Version | undefined> {
  const versions = await getAllVersions();
  const target = await getVersion(versionId);
  if (!target) throw new Error("Target version not found");

  // Trigger the actual pipeline rollback on this stage
  const executionId = await rollbackPipelineStage(targetStage);

  // Mark current version at this stage as rolled-back
  const current = versions.find(
    (v) => v.stage === targetStage && (v.status === "deployed" || v.status === "awaiting-promotion") && v.id !== versionId
  );
  if (current) {
    await updateItem(Tables.VERSIONS, { id: current.id }, { status: "rolled-back" as VersionStatus });
  }

  // Mark the target version as rolled-back too (it was what we're rolling back)
  await updateItem(Tables.VERSIONS, { id: versionId }, {
    status: "rolled-back" as VersionStatus,
    pipelineExecutionId: executionId,
  });

  return { ...target, status: "rolled-back", pipelineExecutionId: executionId };
}

/**
 * Redeploy a version, optionally with a new commit attached (hotfix).
 * Creates a new version entry based on the original but with the additional commit.
 */
export async function redeployVersion(
  versionId: string,
  targetStage: Stage,
  additionalCommitId?: string
): Promise<Version | undefined> {
  const original = await getVersion(versionId);
  if (!original) throw new Error("Version not found");

  let commits = [...original.commits];
  let targetCommitId = original.targetCommitId;
  let changelog = original.changelog;

  // If attaching a new commit (hotfix)
  if (additionalCommitId && !commits.includes(additionalCommitId)) {
    commits.push(additionalCommitId);
    targetCommitId = additionalCommitId; // new commit becomes the build target

    // Regenerate changelog with the new commit
    const allCommits = await getAllCommits();
    const selectedCommits = allCommits.filter((c) => commits.includes(c.commitId));
    changelog = generateChangelog(selectedCommits);
  }

  // Mark current deployed version at target stage as rolled-back
  const versions = await getAllVersions();
  const current = versions.find((v) => v.stage === targetStage && v.status === "deployed");
  if (current) {
    await updateItem(Tables.VERSIONS, { id: current.id }, { status: "rolled-back" as VersionStatus });
  }

  // Create a new version entry for the redeploy
  const newVersion: Version = {
    id: `ver-${Date.now()}`,
    version: additionalCommitId ? `${original.version}-hotfix` : original.version,
    stage: targetStage,
    status: "deploying",
    createdAt: new Date().toISOString(),
    commits,
    changelog,
    linkedFeatures: original.linkedFeatures,
    linkedBugs: original.linkedBugs,
    targetCommitId,
  };

  // Trigger pipeline
  const executionId = await triggerPipelineWithVersion(targetCommitId);
  newVersion.pipelineExecutionId = executionId;

  await putItem(Tables.VERSIONS, newVersion);
  return newVersion;
}

/** Simple rollback — just marks a version as rolled-back (legacy, no pipeline action) */
export async function rollbackVersion(id: string): Promise<Version | undefined> {
  const existing = await getVersion(id);
  if (!existing) return undefined;

  await updateItem(Tables.VERSIONS, { id }, { status: "rolled-back" as VersionStatus });
  return { ...existing, status: "rolled-back" };
}

/**
 * Update manual release notes on a version.
 * Can be edited at any time (even after deploy).
 */
export async function updateVersionNotes(versionId: string, notes: string): Promise<void> {
  await updateItem(Tables.VERSIONS, { id: versionId }, { notes });
}

/**
 * Link a feature to a version (adds to linkedFeatures array).
 */
export async function linkFeatureToVersion(versionId: string, featureId: string): Promise<void> {
  const version = await getVersion(versionId);
  if (!version) return;
  if (!version.linkedFeatures.includes(featureId)) {
    await updateItem(Tables.VERSIONS, { id: versionId }, {
      linkedFeatures: [...version.linkedFeatures, featureId],
    });
  }
}

/**
 * Link a bug to a version (adds to linkedBugs array).
 */
export async function linkBugToVersion(versionId: string, bugId: string): Promise<void> {
  const version = await getVersion(versionId);
  if (!version) return;
  if (!version.linkedBugs.includes(bugId)) {
    await updateItem(Tables.VERSIONS, { id: versionId }, {
      linkedBugs: [...version.linkedBugs, bugId],
    });
  }
}

/** Get commits that aren't assigned to any version yet AND are approved */
export async function getUnversionedCommits(): Promise<Commit[]> {
  const [versions, commits] = await Promise.all([getAllVersions(), getAllCommits()]);
  const versionedIds = new Set(versions.flatMap((v) => v.commits));
  return commits.filter((c) => !versionedIds.has(c.commitId) && c.reviewStatus === "approved");
}

/**
 * Add commits to an existing version that hasn't been deployed yet.
 * Updates the changelog and targetCommitId accordingly.
 */
export async function addCommitsToVersion(
  versionId: string,
  commitIds: string[]
): Promise<Version | undefined> {
  const version = await getVersion(versionId);
  if (!version) throw new Error("Version not found");
  if (version.status !== "created" && version.status !== "failed") {
    throw new Error(`Cannot edit version in status "${version.status}"`);
  }

  // Merge new commits (avoid duplicates)
  const allCommitIds = [...new Set([...version.commits, ...commitIds])];

  // Fetch full commit objects for changelog regeneration
  const allCommitsData = await getAllCommits();
  const selectedCommits = allCommitsData.filter((c) => allCommitIds.includes(c.commitId));

  // Newest commit becomes the new target
  const sorted = [...selectedCommits].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
  const targetCommitId = sorted[0]?.commitId || allCommitIds[0];

  // Reaggregate linked features/bugs
  const linkedFeatures = [...new Set(selectedCommits.flatMap((c) => c.linkedFeatures))];
  const linkedBugs = [...new Set(selectedCommits.flatMap((c) => c.linkedBugs))];

  // Regenerate changelog
  const changelog = generateChangelog(selectedCommits);

  await updateItem(Tables.VERSIONS, { id: versionId }, {
    commits: allCommitIds,
    targetCommitId,
    linkedFeatures,
    linkedBugs,
    changelog,
  });

  return {
    ...version,
    commits: allCommitIds,
    targetCommitId,
    linkedFeatures,
    linkedBugs,
    changelog,
  };
}

/**
 * Remove a commit from a version that hasn't been deployed yet.
 */
export async function removeCommitFromVersion(
  versionId: string,
  commitId: string
): Promise<Version | undefined> {
  const version = await getVersion(versionId);
  if (!version) throw new Error("Version not found");
  if (version.status !== "created" && version.status !== "failed") {
    throw new Error(`Cannot edit version in status "${version.status}"`);
  }

  const remainingCommitIds = version.commits.filter((id) => id !== commitId);
  if (remainingCommitIds.length === 0) {
    throw new Error("Cannot remove the last commit from a version");
  }

  // Refetch and regenerate
  const allCommitsData = await getAllCommits();
  const selectedCommits = allCommitsData.filter((c) => remainingCommitIds.includes(c.commitId));

  const sorted = [...selectedCommits].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
  const targetCommitId = sorted[0]?.commitId || remainingCommitIds[0];

  const linkedFeatures = [...new Set(selectedCommits.flatMap((c) => c.linkedFeatures))];
  const linkedBugs = [...new Set(selectedCommits.flatMap((c) => c.linkedBugs))];
  const changelog = generateChangelog(selectedCommits);

  await updateItem(Tables.VERSIONS, { id: versionId }, {
    commits: remainingCommitIds,
    targetCommitId,
    linkedFeatures,
    linkedBugs,
    changelog,
  });

  return {
    ...version,
    commits: remainingCommitIds,
    targetCommitId,
    linkedFeatures,
    linkedBugs,
    changelog,
  };
}
